/**
 * Chat Interface Component
 * Handles natural language query input and displays conversation history
 */

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { translateQuery, getExampleQueries, conversationService } from '../services/nlpService';
import { executeGISQuery, findNearbyTracts, findNearbyTractsMultiCenter } from '../services/gisService';
import dossierService from '../services/dossierService';
import { Send, Loader, AlertCircle, Lightbulb, Save, FolderOpen, Download, Upload, Trash2, X, Sparkles, ArrowRight } from 'lucide-react';
import { isWaterTenant, isEmissionsTenant } from '../config/gisConfig';

const APP_TITLE = isEmissionsTenant
  ? 'Nexus Emissions'
  : isWaterTenant
    ? 'Nexus Water'
    : 'TPL GIS Query Assistant';
const APP_SUBTITLE = isEmissionsTenant
  ? 'Ask questions about pipelines, leak surveys, and emissions operations using natural language.'
  : isWaterTenant
    ? 'Ask questions about your water assets using natural language.'
    : 'Ask questions about your land, minerals, and well data in natural language.';

// Simple markdown renderer for results (safe - no dangerouslySetInnerHTML)
function FormattedText({ text }) {
  if (!text) return null;

  // Split by newlines and process each line
  const lines = text.split('\n');

  return (
    <span className="formatted-text">
      {lines.map((line, lineIdx) => {
        // Process bold markers **text**
        const parts = [];
        let remaining = line;
        let partIdx = 0;

        while (remaining.length > 0) {
          const boldStart = remaining.indexOf('**');
          if (boldStart === -1) {
            parts.push(<span key={partIdx++}>{remaining}</span>);
            break;
          }

          // Text before bold
          if (boldStart > 0) {
            parts.push(<span key={partIdx++}>{remaining.substring(0, boldStart)}</span>);
          }

          // Find end of bold
          const boldEnd = remaining.indexOf('**', boldStart + 2);
          if (boldEnd === -1) {
            parts.push(<span key={partIdx++}>{remaining.substring(boldStart)}</span>);
            break;
          }

          // Bold text
          const boldText = remaining.substring(boldStart + 2, boldEnd);
          parts.push(<strong key={partIdx++}>{boldText}</strong>);
          remaining = remaining.substring(boldEnd + 2);
        }

        // Handle list items (lines starting with "- ")
        const isBullet = line.trimStart().startsWith('- ');

        return (
          <React.Fragment key={lineIdx}>
            {lineIdx > 0 && <br />}
            {isBullet ? <span>• {parts.length > 0 ? parts : line.replace(/^[\s]*- /, '')}</span> : parts}
          </React.Fragment>
        );
      })}
    </span>
  );
}

const ChatInterface = forwardRef(function ChatInterface({ onQueryResults, onError, onAnalytics, onClearAnalytics, onSymbologyChange }, ref) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showExamples, setShowExamples] = useState(true);
  const [lastResults, setLastResults] = useState(null);
  const lastResultsRef = useRef(null); // Ref for immediate access (React state is async)
  const [analyticsActive, setAnalyticsActive] = useState(false);
  const [lastAnalyticsQuery, setLastAnalyticsQuery] = useState(null);
  const [currentAnalyticsData, setCurrentAnalyticsData] = useState(null);
  const [pendingInteraction, setPendingInteraction] = useState(null); // For smart follow-up questions
  const messagesEndRef = useRef(null);
  const exampleQueries = getExampleQueries();

  // Smart interactive queries - when clicked, ask for user input via chips
  const smartQueries = [
    {
      id: 'filter-county',
      label: '🏛️ Filter by County',
      question: 'Which county would you like to explore?',
      options: ['Reeves', 'Howard', 'Midland', 'Pecos', 'Loving', 'Martin', 'Culberson', 'Ward'],
      template: 'Show tracts in {value} County'
    },
    {
      id: 'filter-operator',
      label: '🏢 Filter by Operator',
      question: 'Which operator are you interested in?',
      options: ['Diamondback', 'EOG', 'ConocoPhillips', 'Mewbourne', 'Pioneer', 'Devon', 'Chevron', 'Occidental'],
      template: 'Show wells operated by {value}'
    },
    {
      id: 'filter-status',
      label: '📊 Filter by Well Status',
      question: 'What well status would you like to see?',
      options: ['Producing', 'DUC', 'Drilling', 'Permitted', 'Shut-in'],
      template: 'Show {value} wells'
    },
    {
      id: 'filter-formation',
      label: '⛏️ Filter by Formation',
      question: 'Which formation/interval?',
      options: ['Wolfcamp', 'Bone Spring', 'Spraberry', 'Delaware', 'Avalon'],
      template: 'Show wells in {value}'
    },
    {
      id: 'filter-acreage',
      label: '📏 Filter by Acreage',
      question: 'What acreage threshold?',
      options: ['Over 100 acres', 'Over 200 acres', 'Over 500 acres', 'Under 50 acres'],
      template: 'Show tracts {value}'
    },
    {
      id: 'filter-contract',
      label: '📝 Filter by Contract Type',
      question: 'What type of contract?',
      options: ['Lease', 'Easement', 'Surface', 'Mineral', 'ROW'],
      template: 'Show {value} contracts'
    }
  ];

  // Saved Analysis (Dossier) state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadPanel, setShowLoadPanel] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [analysisName, setAnalysisName] = useState('');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const fileInputRef = useRef(null);

  // Expose sendQuery method to parent components via ref
  useImperativeHandle(ref, () => ({
    sendQuery: (query) => {
      console.log('📤 External query received:', query);
      handleSubmit(null, query);
    }
  }));

  // Load saved analyses on mount
  useEffect(() => {
    async function loadAnalyses() {
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);
    }
    loadAnalyses();
  }, []);

  // Track analytics data for saving
  useEffect(() => {
    // This effect captures analytics data when it changes
    // We'll use a custom event or callback approach
  }, []);

  // Auto-scroll to bottom when new messages arrive (only if there are messages)
  useEffect(() => {
    // Don't auto-scroll on welcome screen - only scroll when there are actual messages
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Auto-refresh analytics when lastResults changes (if analytics mode is active)
  useEffect(() => {
    async function refreshAnalytics() {
      if (analyticsActive && lastAnalyticsQuery) {
        // If results are empty, clear analytics
        if (!lastResults || lastResults.length === 0) {
          console.log('🔄 Query returned 0 results - clearing analytics');
          if (onClearAnalytics) {
            onClearAnalytics();
          }
          return;
        }

        // Otherwise, refresh analytics with new data
        console.log('🔄 Auto-refreshing analytics for updated results. Count:', lastResults.length);

        // Get enriched summary with contract data
        const summary = await conversationService.getEnrichedSummary(lastResults);

        // Update analytics panel with refreshed data
        if (onAnalytics) {
          onAnalytics({
            query: lastAnalyticsQuery,
            summary: summary
          });
        }
      }
    }

    refreshAnalytics();
  }, [lastResults, analyticsActive, lastAnalyticsQuery, onAnalytics, onClearAnalytics]);

  // Handle query submission
  const handleSubmit = async (e, customQuery = null) => {
    if (e) e.preventDefault();

    const query = customQuery || input;
    if (!query.trim()) return;

    setShowExamples(false);
    setInput('');
    setLoading(true);

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Check if this is a symbology/color change request
      if (conversationService.isSymbologyChangeQuery(query)) {
        const symbologyChange = conversationService.parseSymbologyChange(query);
        if (symbologyChange && onSymbologyChange) {
          console.log('🎨 Symbology change detected:', symbologyChange);
          onSymbologyChange(symbologyChange);

          // Add confirmation message
          const targetLabel = symbologyChange.target === 'wellStatus' ? 'well status' :
                              symbologyChange.target === 'tractType' ? 'tract type' :
                              symbologyChange.target === 'allWells' ? 'all wells' :
                              symbologyChange.target === 'allTracts' ? 'all tracts' : 'features';
          const confirmMessage = {
            id: Date.now() + 1,
            type: 'assistant',
            content: `🎨 Updated map symbology! Changed ${targetLabel} "${symbologyChange.value}" to color ${symbologyChange.color}. The map will reflect this change.`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, confirmMessage]);
          setLoading(false);
          return;
        }
      }

      // Check if this is a filter query on existing results
      const isFilter = conversationService.isFilterQuery(query);
      const hasResults = lastResults && lastResults.length > 0;

      console.log('🔍 Filter check:', {
        query,
        isFilter,
        hasResults,
        lastResultsCount: lastResults?.length || 0
      });

      if (isFilter && hasResults) {
        console.log('✅ Filter query detected. Filtering existing results...');

        // Filter existing results
        const filteredResults = conversationService.filterResults(lastResults, query);
        console.log(`📊 Filtered ${lastResults.length} → ${filteredResults.length} results`);

        // Add assistant thinking message
        const thinkingMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: `Filtering current results based on: "${query}"`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, thinkingMessage]);

        // Display filtered results
        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Filtered to ${filteredResults.length} feature${filteredResults.length !== 1 ? 's' : ''} (from ${lastResults.length} total).`,
          results: {
            features: filteredResults,
            count: filteredResults.length,
            layer: 'tpl-tracts',
            query: query
          },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        // Update stored results (both state and ref for immediate access)
        lastResultsRef.current = filteredResults;
        setLastResults(filteredResults);
        conversationService.storeQuery({ layer: 'tpl-tracts', description: query }, filteredResults);

        // Determine if this filter should trigger a zoom
        // Zoom when: county filter, operator filter, or significant result reduction
        const isCountyFilter = /\b(county|culberson|reeves|howard|loving|martin|midland|ector|ward|winkler|andrews|pecos|crane|glasscock|reagan|lea|eddy|chaves)\b/i.test(query);
        const isOperatorFilter = /\b(diamondback|eog|mewbourne|pioneer|chevron|exxon|bp|shell|apache|devon|occidental|ovintiv|conoco|conocophillips|coterra|marathon|cimarex|centennial|parsley|callon|energen|fasken|henry|encana|anadarko|noble|laredo|matador|highpeak|endeavor|summit|grenadier|birch)\b/i.test(query);
        const isSignificantReduction = filteredResults.length > 0 && filteredResults.length < lastResults.length * 0.8;
        const shouldZoom = isCountyFilter || isOperatorFilter || isSignificantReduction;

        // Calculate extent from filtered results for proper zooming
        let extent = null;
        if (shouldZoom && filteredResults.length > 0) {
          let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
          filteredResults.forEach(feature => {
            const coords = feature.geometry?.coordinates;
            const type = feature.geometry?.type;
            if (!coords) return;

            const updateBounds = (coordArray) => {
              if (Array.isArray(coordArray[0])) {
                coordArray.forEach(updateBounds);
              } else {
                const [lon, lat] = coordArray;
                if (lon < xmin) xmin = lon;
                if (lon > xmax) xmax = lon;
                if (lat < ymin) ymin = lat;
                if (lat > ymax) ymax = lat;
              }
            };
            updateBounds(coords);
          });
          if (xmin !== Infinity) {
            extent = { xmin, ymin, xmax, ymax };
          }
        }

        // Pass filtered results to parent (map component)
        if (onQueryResults) {
          onQueryResults({
            features: filteredResults,
            count: filteredResults.length,
            layer: 'tpl-tracts',
            query: query,
            zoom: shouldZoom, // Zoom on county filter or significant reduction
            extent: extent // Include extent for proper zoom bounds
          });
        }

        // Smart Analyst: Generate insights for filtered results
        if (filteredResults.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(filteredResults, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return;
      }

      // Step 1: Translate natural language to GIS query
      const intent = await translateQuery(query, lastResults);

      // Add assistant thinking message
      const thinkingMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I understand you want to: ${intent.description}`,
        intent: intent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, thinkingMessage]);

      // Check if this is an analytics query
      if (intent.layer === 'analytics') {
        // Handle analytics query - don't query the map
        // Use ref for immediate access (state may not have updated yet in sequential queries)
        const currentResults = lastResultsRef.current || lastResults;
        console.log('Analytics query detected. currentResults:', currentResults?.length);
        if (!currentResults || currentResults.length === 0) {
          throw new Error('Please run a search first to analyze the results.');
        }

        // Generate analytics response
        const analyticsText = conversationService.generateAnalytics(query, currentResults);

        // Get enriched summary with contract data
        const summary = await conversationService.getEnrichedSummary(currentResults);

        const analyticsMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: analyticsText,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, analyticsMessage]);

        // Enable analytics mode and store query for auto-refresh
        setAnalyticsActive(true);
        setLastAnalyticsQuery(query);

        // Pass analytics data to parent for display in AnalyticsPanel
        if (onAnalytics) {
          console.log('ChatInterface calling onAnalytics with:', { query, summary });
          onAnalytics({
            query: query,
            summary: summary
          });
        } else {
          console.warn('onAnalytics callback not provided!');
        }

        return; // Don't execute GIS query or update map
      }

      // Check if this is a contract-based query
      if (intent.layer === 'contracts') {
        console.log('Contract query detected:', intent);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

        // Build query params from contractFilters
        const params = new URLSearchParams();
        if (intent.contractFilters.minRevenue) {
          params.append('minRevenue', intent.contractFilters.minRevenue);
        }
        if (intent.contractFilters.maxRevenue) {
          params.append('maxRevenue', intent.contractFilters.maxRevenue);
        }
        if (intent.contractFilters.status) {
          params.append('status', intent.contractFilters.status);
        }
        if (intent.contractFilters.contractType) {
          params.append('contractType', intent.contractFilters.contractType);
        }
        if (intent.contractFilters.operator) {
          params.append('operator', intent.contractFilters.operator);
        }

        // Step 1: Get tract numbers from contracts
        const contractResponse = await fetch(`${backendUrl}/api/contracts/tract-numbers?${params}`);
        if (!contractResponse.ok) {
          throw new Error('Failed to fetch contract data');
        }
        const contractData = await contractResponse.json();
        const tractNumbers = contractData.data;

        if (tractNumbers.length === 0) {
          throw new Error('No tracts found matching those contract criteria.');
        }

        console.log(`Found ${tractNumbers.length} tracts from contract query`);

        // Step 2: Query GIS layer with those tract numbers
        const gisQuery = {
          layer: 'tracts',
          conditions: [{
            field: 'TRACT_NUMBER',
            operator: 'IN',
            value: tractNumbers,
            type: 'string'
          }],
          description: intent.description,
          zoom: false
        };

        // Execute GIS query to get tract geometries
        const results = await executeGISQuery(gisQuery);

        // Store results for follow-up queries
        conversationService.storeQuery(gisQuery, results.features);

        // Send results to map
        if (onQueryResults) {
          onQueryResults({
            features: results.features,
            count: results.count,
            layer: 'tracts',
            zoom: false
          });
        }

        // Create result message
        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${results.count}** tract${results.count !== 1 ? 's' : ''} matching contract criteria.`,
          resultCount: results.count,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        // Store results for analytics (both state and ref)
        lastResultsRef.current = results.features;
        setLastResults(results.features);

        return; // Don't continue to normal query flow
      }

      // Check if this is a proximity query
      if (intent.layer === 'proximity') {
        console.log('Proximity query detected:', intent);

        // Load tract data from backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
        const tractDataResponse = await fetch(`${backendUrl}/api/mock/tracts/query?where=1=1&f=json`);

        if (!tractDataResponse.ok) {
          throw new Error('Failed to load tract data');
        }

        const tractData = await tractDataResponse.json();
        const allFeatures = tractData.features || [];

        // Determine if single-center or multi-center query
        const isMultiCenter = intent.centerTracts && Array.isArray(intent.centerTracts);
        let proximityResults;

        if (isMultiCenter) {
          // Multi-center proximity search
          proximityResults = findNearbyTractsMultiCenter(
            intent.centerTracts,
            allFeatures,
            intent.radius,
            intent.unit || 'miles'
          );
          console.log('Multi-center proximity results:', proximityResults);
        } else {
          // Single-center proximity search
          proximityResults = findNearbyTracts(
            intent.centerTract,
            allFeatures,
            intent.radius,
            intent.unit || 'miles'
          );
          // Normalize to array format for consistent handling
          proximityResults.centerTracts = [proximityResults.centerTract];
          console.log('Single-center proximity results:', proximityResults);
        }

        // Apply attribute filters if conditions are provided (combined proximity + attribute query)
        let filteredNearbyTracts = proximityResults.nearbyTracts;

        if (intent.conditions && intent.conditions.length > 0) {
          filteredNearbyTracts = proximityResults.nearbyTracts.filter(tractData => {
            const feature = tractData.feature;
            const props = feature.attributes || feature.properties || {};

            // Check all conditions (AND logic)
            return intent.conditions.every(condition => {
              const fieldValue = props[condition.field];
              const condValue = condition.value;

              // Handle different operators
              switch (condition.operator) {
                case '=':
                  return String(fieldValue).toLowerCase() === String(condValue).toLowerCase();
                case 'LIKE':
                  return String(fieldValue).toLowerCase().includes(String(condValue).replace(/%/g, '').toLowerCase());
                case '>':
                  return parseFloat(fieldValue) > parseFloat(condValue);
                case '<':
                  return parseFloat(fieldValue) < parseFloat(condValue);
                case '>=':
                  return parseFloat(fieldValue) >= parseFloat(condValue);
                case '<=':
                  return parseFloat(fieldValue) <= parseFloat(condValue);
                case 'IN':
                  const values = Array.isArray(condValue) ? condValue : [condValue];
                  return values.some(v => String(fieldValue).toLowerCase() === String(v).toLowerCase());
                case 'BETWEEN':
                  const rangeValues = Array.isArray(condValue) ? condValue : [condValue, condValue];
                  const numValue = parseFloat(fieldValue);
                  return numValue >= parseFloat(rangeValues[0]) && numValue <= parseFloat(rangeValues[1]);
                default:
                  return true;
              }
            });
          });
        }

        // Create result message with filter info if applicable
        const centerDescription = isMultiCenter
          ? `tracts ${intent.centerTracts.join(', ')}`
          : `tract ${intent.centerTract}`;

        const proximityMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: intent.conditions && intent.conditions.length > 0
            ? `Found ${filteredNearbyTracts.length} tract${filteredNearbyTracts.length !== 1 ? 's' : ''} matching criteria within ${intent.radius} ${intent.unit} of ${centerDescription} (${proximityResults.totalFound} total nearby).`
            : `Found ${filteredNearbyTracts.length} tract${filteredNearbyTracts.length !== 1 ? 's' : ''} within ${intent.radius} ${intent.unit} of ${centerDescription}.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, proximityMessage]);

        // Store proximity results for follow-up queries (including analytics)
        const proximityFeatures = filteredNearbyTracts.map(t => t.feature);
        lastResultsRef.current = proximityFeatures;
        setLastResults(proximityFeatures);
        conversationService.storeQuery(intent, proximityFeatures);

        // Pass proximity results to parent (map component)
        if (onQueryResults) {
          onQueryResults({
            features: proximityFeatures,
            count: filteredNearbyTracts.length,
            layer: 'proximity',
            zoom: true,
            proximityData: {
              centerTracts: proximityResults.centerTracts, // Array of center tracts
              radius: intent.radius,
              unit: intent.unit || 'miles',
              nearbyTracts: filteredNearbyTracts, // Use filtered results for map display
              isMultiCenter: isMultiCenter
            }
          });
        }

        // Smart Analyst: Generate insights for proximity results
        if (proximityFeatures.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(proximityFeatures, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return; // Don't execute normal GIS query
      }

      // Check if this is a multi-layer query (WOW FEATURE!)
      if (intent.layer === 'multi' && intent.layers && intent.layers.length > 0) {
        console.log('🌟 Multi-layer query detected:', intent.layers);

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
        const allFeatures = [];
        const layerResults = {};

        // Field mapping for different layers (county field varies by layer)
        const fieldMapping = {
          tracts: { County: 'CountyName' },
          wells: { CountyName: 'County' },
          contracts: { CountyName: 'County' }
        };

        // Query each layer in parallel
        const layerPromises = intent.layers.map(async (layerKey) => {
          try {
            // Adjust conditions for layer-specific field names
            const adjustedConditions = intent.conditions?.map(cond => {
              const mapping = fieldMapping[layerKey];
              if (mapping && mapping[cond.field]) {
                return { ...cond, field: mapping[cond.field] };
              }
              return cond;
            }) || [];

            // Build WHERE clause
            const whereParts = adjustedConditions.map(cond => {
              if (cond.type === 'string') {
                if (cond.operator === 'LIKE') {
                  return `${cond.field} ${cond.operator} '${cond.value}'`;
                }
                return `${cond.field} ${cond.operator} '${cond.value}'`;
              }
              return `${cond.field} ${cond.operator} ${cond.value}`;
            });
            const whereClause = whereParts.length > 0 ? whereParts.join(' AND ') : '1=1';

            const url = `${backendUrl}/api/geojson/${layerKey}?where=${encodeURIComponent(whereClause)}`;
            console.log(`📍 Querying ${layerKey}:`, url);

            const response = await fetch(url);
            if (!response.ok) {
              console.warn(`Failed to query ${layerKey}:`, response.status);
              return { layerKey, features: [], count: 0 };
            }

            const data = await response.json();
            const features = (data.features || []).map(f => ({
              ...f,
              _sourceLayer: layerKey // Tag each feature with its source layer
            }));

            return { layerKey, features, count: features.length };
          } catch (err) {
            console.warn(`Error querying ${layerKey}:`, err);
            return { layerKey, features: [], count: 0 };
          }
        });

        const results = await Promise.all(layerPromises);

        // Combine all results
        let totalCount = 0;
        results.forEach(({ layerKey, features, count }) => {
          layerResults[layerKey] = { features, count };
          allFeatures.push(...features);
          totalCount += count;
        });

        console.log('🌟 Multi-layer results:', {
          totalCount,
          byLayer: Object.fromEntries(Object.entries(layerResults).map(([k, v]) => [k, v.count]))
        });

        // Create result message showing breakdown by layer
        const layerBreakdown = results
          .filter(r => r.count > 0)
          .map(r => `${r.count} ${r.layerKey}`)
          .join(', ');

        const multiResultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${totalCount}** features across ${intent.layers.length} layers: ${layerBreakdown}`,
          results: {
            features: allFeatures,
            count: totalCount,
            layer: 'multi',
            layers: intent.layers,
            layerResults: layerResults
          },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, multiResultMessage]);

        // Calculate extent from all features
        let extent = null;
        if (allFeatures.length > 0) {
          let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
          allFeatures.forEach(feature => {
            const coords = feature.geometry?.coordinates;
            if (!coords) return;

            const updateBounds = (coordArray) => {
              if (Array.isArray(coordArray[0])) {
                coordArray.forEach(updateBounds);
              } else {
                const [lon, lat] = coordArray;
                if (lon < xmin) xmin = lon;
                if (lon > xmax) xmax = lon;
                if (lat < ymin) ymin = lat;
                if (lat > ymax) ymax = lat;
              }
            };
            updateBounds(coords);
          });
          if (xmin !== Infinity) {
            extent = { xmin, ymin, xmax, ymax };
          }
        }

        // Store results
        lastResultsRef.current = allFeatures;
        setLastResults(allFeatures);
        conversationService.storeQuery(intent, allFeatures);

        // Pass multi-layer results to map
        if (onQueryResults) {
          onQueryResults({
            features: allFeatures,
            count: totalCount,
            layer: 'multi',
            layers: intent.layers,
            layerResults: layerResults,
            zoom: intent.zoom !== false,
            extent: extent,
            isMultiLayer: true
          });
        }

        // Smart Analyst for multi-layer results
        if (allFeatures.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(allFeatures, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return; // Don't execute normal GIS query
      }

      // Step 2: Execute GIS query (for spatial queries)
      const results = await executeGISQuery(intent);

      // Store results for follow-up queries (both ref for immediate access and state)
      console.log('Storing results. features count:', results.features?.length);
      lastResultsRef.current = results.features;
      setLastResults(results.features);
      conversationService.storeQuery(intent, results.features);
      console.log('Results stored in ref (immediate) and state.');

      // Step 3: Display results
      const resultMessage = {
        id: Date.now() + 2,
        type: 'result',
        content: `Found ${results.count} feature${results.count !== 1 ? 's' : ''} matching your query.`,
        results: results,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, resultMessage]);

      // Pass results to parent (map component)
      if (onQueryResults) {
        onQueryResults(results);
      }

      // Smart Analyst: Generate proactive insights and suggestions
      if (results.features && results.features.length > 0) {
        const smartInsights = conversationService.generateSmartInsights(results.features, query);
        if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
          const insightsMessage = {
            id: Date.now() + 3,
            type: 'smart-analyst',
            insights: smartInsights.insights,
            suggestions: smartInsights.suggestions,
            summary: smartInsights.summary,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, insightsMessage]);
        }
      }

    } catch (error) {
      console.error('Query failed:', error);

      const errorMessage = {
        id: Date.now() + 3,
        type: 'error',
        content: error.message || 'Sorry, I couldn\'t process that query. Please try rephrasing.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle example query click
  const handleExampleClick = (exampleQuery) => {
    setInput(exampleQuery);
    handleSubmit(null, exampleQuery);
  };

  // Extract unique values from current results for dynamic filtering
  const getUniqueValuesFromResults = (fieldNames) => {
    if (!lastResults || lastResults.length === 0) return [];

    const values = new Set();
    lastResults.forEach(feature => {
      const props = feature.properties || feature.attributes || {};
      for (const fieldName of fieldNames) {
        const value = props[fieldName];
        if (value && value.toString().trim()) {
          values.add(value.toString().trim());
        }
      }
    });

    // Return sorted, limited to top 8
    return Array.from(values).sort().slice(0, 8);
  };

  // Handle smart query click - shows follow-up question with chips
  const handleSmartQueryClick = (smartQuery) => {
    // Build dynamic options based on current results
    let dynamicOptions = smartQuery.options; // fallback to defaults

    if (lastResults && lastResults.length > 0) {
      switch (smartQuery.id) {
        case 'filter-county':
          dynamicOptions = getUniqueValuesFromResults(['CountyName', 'County', 'COUNTY']);
          break;
        case 'filter-operator':
          dynamicOptions = getUniqueValuesFromResults(['ENVOperator', 'Operator', 'OPERATOR']);
          break;
        case 'filter-status':
          dynamicOptions = getUniqueValuesFromResults(['ENVWellStatus', 'WellStatus', 'Status']);
          break;
        case 'filter-formation':
          dynamicOptions = getUniqueValuesFromResults(['ENVInterval', 'Formation', 'Interval']);
          break;
        case 'filter-contract':
          dynamicOptions = getUniqueValuesFromResults(['ContractType', 'Type']);
          break;
        case 'filter-acreage':
          // For acreage, keep static options as they're ranges
          dynamicOptions = smartQuery.options;
          break;
        default:
          dynamicOptions = smartQuery.options;
      }
    }

    // If no values found, use defaults
    if (!dynamicOptions || dynamicOptions.length === 0) {
      dynamicOptions = smartQuery.options;
    }

    // Add assistant message asking the question
    const questionMessage = {
      id: Date.now(),
      type: 'assistant',
      content: smartQuery.question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, questionMessage]);
    setShowExamples(false);

    // Set pending interaction with dynamic options
    setPendingInteraction({
      ...smartQuery,
      options: dynamicOptions
    });
  };

  // Handle chip selection from smart query
  const handleChipSelect = (value) => {
    if (!pendingInteraction) return;

    // Build the actual query from template
    const query = pendingInteraction.template.replace('{value}', value);

    // Clear pending interaction
    setPendingInteraction(null);

    // Execute the query
    setInput(query);
    handleSubmit(null, query);
  };

  // Cancel pending interaction
  const handleCancelInteraction = () => {
    setPendingInteraction(null);
    // Remove the question message
    setMessages(prev => prev.slice(0, -1));
  };

  // Show examples without clearing results
  const handleShowExamples = () => {
    setShowExamples(true);
  };

  // Clear conversation
  const handleClear = () => {
    setMessages([]);
    setShowExamples(true);
    setInput('');
    lastResultsRef.current = null;
    setLastResults(null);
    setAnalyticsActive(false);
    setLastAnalyticsQuery(null);
    setCurrentAnalyticsData(null);
    conversationService.clear();

    // Clear analytics panel
    if (onClearAnalytics) {
      onClearAnalytics();
    }

    // Clear map markers and polygons
    if (onQueryResults) {
      onQueryResults(null);
    }
  };

  // ===== SAVED ANALYSIS FUNCTIONS =====

  // Save current analysis
  const handleSaveAnalysis = async () => {
    if (!analysisName.trim()) {
      alert('Please enter a name for this analysis');
      return;
    }

    // Extract queries from messages
    const userQueries = messages
      .filter(m => m.type === 'user')
      .map(m => m.content);

    // Get last query
    const lastQuery = userQueries[userQueries.length - 1] || '';

    // Try to extract county/operator from queries
    let county = '';
    let operator = '';
    for (const q of userQueries) {
      const countyMatch = q.match(/\b(Howard|Reeves|Loving|Martin|Midland|Lea|Culberson|Ward|Pecos|Ector)\b/i);
      if (countyMatch) county = countyMatch[1];
      const opMatch = q.match(/\b(Diamondback|EOG|Occidental|Mewbourne|Devon|ConocoPhillips|Exxon)\b/i);
      if (opMatch) operator = opMatch[1];
    }

    try {
      const saved = await dossierService.saveDossier({
        name: analysisName.trim(),
        description: analysisDescription.trim(),
        queries: userQueries,
        lastQuery: lastQuery,
        layer: 'tracts',
        resultCount: lastResults?.length || 0,
        analyticsData: currentAnalyticsData,
        county: county,
        operator: operator
      });

      // Update local state
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);

      // Reset modal
      setShowSaveModal(false);
      setAnalysisName('');
      setAnalysisDescription('');

      // Show success message
      const successMsg = {
        id: Date.now(),
        type: 'assistant',
        content: `Analysis saved to database as "${saved.name}". This will persist even if you close your browser.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMsg]);

    } catch (error) {
      alert('Error saving analysis: ' + error.message);
    }
  };

  // Check if a query is an analytics/summary query (should run after data queries)
  const isAnalyticsQuery = (query) => {
    const analyticsKeywords = [
      'summarize', 'summary', 'analyze', 'analytics', 'breakdown',
      'group by', 'count by', 'total by', 'aggregate', 'statistics'
    ];
    const lowerQuery = query.toLowerCase();
    return analyticsKeywords.some(keyword => lowerQuery.includes(keyword));
  };

  // Load a saved analysis
  const handleLoadAnalysis = async (analysis) => {
    setShowLoadPanel(false);

    // Clear current state
    handleClear();

    // Re-run the queries from the saved analysis
    if (analysis.queries && analysis.queries.length > 0) {
      // Add a loading message
      const loadingMsg = {
        id: Date.now(),
        type: 'assistant',
        content: `Loading analysis "${analysis.name}"...`,
        timestamp: new Date()
      };
      setMessages([loadingMsg]);
      setShowExamples(false);

      // Separate data queries from analytics queries
      // Data queries must run first, analytics queries run last
      const dataQueries = analysis.queries.filter(q => !isAnalyticsQuery(q));
      const analyticsQueries = analysis.queries.filter(q => isAnalyticsQuery(q));

      // Reorder: data queries first, then analytics queries
      const orderedQueries = [...dataQueries, ...analyticsQueries];

      // Execute queries sequentially with delays
      // First query should zoom to results
      const executeQueriesSequentially = async (queries, index = 0) => {
        if (index >= queries.length) return;

        const query = queries[index];
        const isFirstDataQuery = index === 0 && !isAnalyticsQuery(query);

        // For the first data query, we want to zoom
        // We'll handle this by checking in handleSubmit or by modifying the query context
        console.log(`📋 Loading query ${index + 1}/${queries.length}: "${query}" (zoom: ${isFirstDataQuery})`);

        // Execute the query
        await new Promise(resolve => {
          setTimeout(async () => {
            await handleSubmit(null, query);
            resolve();
          }, index === 0 ? 500 : 1500); // First query 500ms, subsequent 1500ms to allow processing
        });

        // Continue to next query
        if (index < queries.length - 1) {
          executeQueriesSequentially(queries, index + 1);
        }
      };

      executeQueriesSequentially(orderedQueries);
    }
  };

  // Delete a saved analysis
  const handleDeleteAnalysis = async (id, name) => {
    if (window.confirm(`Delete "${name}"? This cannot be undone.`)) {
      await dossierService.deleteDossier(id);
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);
    }
  };

  // Export a saved analysis
  const handleExportAnalysis = async (id) => {
    await dossierService.exportDossier(id);
  };

  // Import analysis from file
  const handleImportAnalysis = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const count = await dossierService.importDossier(e.target.result);
        const analyses = await dossierService.getAllDossiers();
        setSavedAnalyses(analyses);
        alert(`Successfully imported ${count} analysis${count !== 1 ? 'es' : ''}`);
      } catch (error) {
        alert('Error importing: ' + error.message);
      }
    };
    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format date for display
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chat-interface">
      <div className="chat-header chat-header-compact">
        <div className="header-buttons">
          {/* Saved Analysis buttons */}
          <button
            onClick={() => setShowLoadPanel(!showLoadPanel)}
            className="header-btn load-btn"
            title="Load Saved Analysis"
          >
            <FolderOpen size={16} />
            {savedAnalyses.length > 0 && (
              <span className="badge">{savedAnalyses.length}</span>
            )}
          </button>
          {messages.length > 0 && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="header-btn save-btn"
              title="Save Current Analysis"
            >
              <Save size={16} />
            </button>
          )}
          {messages.length > 0 && !showExamples && (
            <button
              onClick={handleShowExamples}
              className="header-btn examples-btn"
              title="Show Examples"
            >
              <Lightbulb size={16} />
            </button>
          )}
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="header-btn clear-btn"
              title="Clear Chat"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Save Analysis Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="save-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save Analysis</h3>
              <button onClick={() => setShowSaveModal(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={analysisName}
                  onChange={e => setAnalysisName(e.target.value)}
                  placeholder="e.g., Howard County Q4 Review"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={analysisDescription}
                  onChange={e => setAnalysisDescription(e.target.value)}
                  placeholder="Notes about this analysis..."
                  rows={3}
                />
              </div>
              <div className="modal-info">
                <p><strong>Queries:</strong> {messages.filter(m => m.type === 'user').length}</p>
                <p><strong>Results:</strong> {lastResults?.length || 0} features</p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowSaveModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleSaveAnalysis} className="save-action-btn">
                <Save size={16} /> Save Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Analysis Modal */}
      {showLoadPanel && (
        <div className="modal-overlay" onClick={() => setShowLoadPanel(false)}>
          <div className="load-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Saved Analyses</h3>
              <div className="load-panel-actions">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportAnalysis}
                  accept=".json"
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="import-btn"
                  title="Import from file"
                >
                  <Upload size={16} />
                </button>
                <button onClick={() => setShowLoadPanel(false)} className="close-btn">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="load-modal-content">
              {savedAnalyses.length === 0 ? (
                <div className="empty-state">
                  <FolderOpen size={32} />
                  <p>No saved analyses yet</p>
                  <small>Run a query and click Save to create one</small>
                </div>
              ) : (
                <div className="saved-list">
                  {savedAnalyses.map(analysis => (
                    <div key={analysis.id} className="saved-item">
                      <div className="saved-item-main" onClick={() => handleLoadAnalysis(analysis)}>
                        <div className="saved-item-name">{analysis.name}</div>
                        <div className="saved-item-meta">
                          <span>{formatDate(analysis.createdAt)}</span>
                          {analysis.county && <span className="tag">{analysis.county}</span>}
                          {analysis.operator && <span className="tag">{analysis.operator}</span>}
                        </div>
                        {analysis.description && (
                          <div className="saved-item-desc">{analysis.description}</div>
                        )}
                        <div className="saved-item-stats">
                          <span>{analysis.queries?.length || 0} queries</span>
                          <span>{analysis.resultCount || 0} results</span>
                        </div>
                      </div>
                      <div className="saved-item-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExportAnalysis(analysis.id); }}
                          title="Export"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAnalysis(analysis.id, analysis.name); }}
                          title="Delete"
                          className="delete-btn"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="chat-messages">
        {messages.length === 0 && showExamples ? (
          <div className="welcome-screen">
            <h3>👋 Welcome to {APP_TITLE}!</h3>
            <p>{APP_SUBTITLE}</p>

            <div className="examples-section">
              <div className="examples-header">
                <Lightbulb size={20} />
                <h4>Try these examples:</h4>
              </div>

              {exampleQueries.map((category, idx) => (
                <div key={idx} className="example-category">
                  <h5>{category.category}</h5>
                  {category.queries.map((query, qIdx) => (
                    <button
                      key={qIdx}
                      className="example-query"
                      onClick={() => handleExampleClick(query)}
                    >
                      {query}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(message => (
              <div key={message.id} className={`message message-${message.type}`}>
                {message.type === 'user' && (
                  <div className="message-content">
                    <strong>You:</strong> {message.content}
                  </div>
                )}

                {message.type === 'assistant' && (
                  <div className="message-content">
                    <strong>Assistant:</strong> {message.content}
                    {message.intent && message.intent.confidence < 0.7 && (
                      <div className="confidence-warning">
                        <AlertCircle size={16} />
                        <span>Confidence: {(message.intent.confidence * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                )}

                {message.type === 'result' && (
                  <div className="message-content">
                    <strong>Results:</strong> <FormattedText text={message.content} />
                    {message.results && message.results.count > 0 && (
                      <div className="result-summary">
                        <p>Layer: <strong>{message.results.layer}</strong></p>
                        <p>Query: <code>{message.results.query}</code></p>
                      </div>
                    )}
                  </div>
                )}

                {message.type === 'error' && (
                  <div className="message-content error">
                    <AlertCircle size={20} />
                    <strong>Error:</strong> {message.content}
                  </div>
                )}

                {message.type === 'smart-analyst' && (
                  <div className="message-content smart-analyst">
                    <div className="smart-analyst-header">
                      <Sparkles size={18} className="sparkle-icon" />
                      <strong>Smart Analyst</strong>
                    </div>

                    {message.insights && message.insights.length > 0 && (
                      <div className="smart-insights">
                        {message.insights.map((insight, idx) => (
                          <div key={idx} className="insight-item">
                            <FormattedText text={insight} />
                          </div>
                        ))}
                      </div>
                    )}

                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="smart-suggestions">
                        <div className="suggestions-header">Would you like to:</div>
                        <div className="suggestion-buttons">
                          {message.suggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              className="suggestion-btn"
                              onClick={() => handleSubmit(null, suggestion.query)}
                            >
                              <ArrowRight size={14} />
                              {suggestion.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Smart Filters inside Smart Analyst */}
                    <div className="smart-refine-section">
                      <div className="suggestions-header">Refine your results:</div>
                      <div className="smart-refine-buttons">
                        {smartQueries.map((sq) => (
                          <button
                            key={sq.id}
                            className="smart-refine-btn"
                            onClick={() => handleSmartQueryClick(sq)}
                          >
                            {sq.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}

            {/* Smart Query Chips - shown when user clicks a smart filter */}
            {pendingInteraction && (
              <div className="smart-chips-container">
                <div className="smart-chips-grid">
                  {pendingInteraction.options.map((option, idx) => (
                    <button
                      key={idx}
                      className="smart-chip"
                      onClick={() => handleChipSelect(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                <button
                  className="smart-chips-cancel"
                  onClick={handleCancelInteraction}
                >
                  <X size={14} /> Cancel
                </button>
              </div>
            )}

            {/* Show examples after messages if requested */}
            {showExamples && messages.length > 0 && (
              <div className="examples-section" style={{ marginTop: '1rem' }}>
                <div className="examples-header">
                  <Lightbulb size={20} />
                  <h4>Try these examples:</h4>
                </div>

                {exampleQueries.map((category, idx) => (
                  <div key={idx} className="example-category">
                    <h5>{category.category}</h5>
                    {category.queries.map((query, qIdx) => (
                      <button
                        key={qIdx}
                        className="example-query"
                        onClick={() => handleExampleClick(query)}
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {loading && (
          <div className="message message-loading">
            <Loader className="spinner" size={20} />
            <span>Processing your query...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="chat-input-form">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your GIS data... (e.g., 'Show me tract 12345')"
          disabled={loading}
          className="chat-input"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="send-button"
        >
          <Send size={20} />
        </button>
      </form>

      <div className="chat-footer">
        <small>
          Powered by Codvo.AI NeIO · Queries are translated to GIS operations
        </small>
      </div>
    </div>
  );
});

export default ChatInterface;
